import type { ChartColors, ChartLineWeight, UserPreferences } from "@/types/users";

/**
 * Chart preferences — how every chart draws: line weight and colours
 * (Preferences → Charts). Server-side user preferences
 * (`session.preferences`), so they follow the user to every browser and reach
 * the scheduled-report renderer, which signs in as the report's owner in a
 * fresh browser.
 *
 * On screen each is a class on `<html>` that `index.css` keys off
 * (§ "Chart line weight", § "Chart colors"); canvas charts pick a change up
 * through `useChartTokens`, which re-reads on every class swap on `<html>`.
 * `AppShell` applies the session's (`useApplyPreferences`). A PDF's print
 * stage wears the classes itself (`printStageClassName`), so it never depends
 * on when `<html>` was updated.
 *
 * `localStorage` holds a copy of the classes only so the pre-paint script in
 * `index.html` can draw the last look before the session arrives. The
 * session wins.
 */

/** The preferences this module draws charts by. */
export type ChartPrefs = Pick<UserPreferences, "chart_line_weight" | "chart_colors">;

export interface ChartWeightVariant {
  id: ChartLineWeight;
  label: string;
  description: string;
  /**
   * Class applied to `<html>`, to a print stage, or to a preview drawn at
   * this weight. Every weight has one, `standard` included, so a preview can
   * show it inside a page set to the other; an unclassed element draws at
   * standard too.
   */
  className: string;
}

export interface ChartColorVariant {
  id: ChartColors;
  label: string;
  description: string;
  /** Class applied to `<html>` or a print stage. `null` for the default. */
  className: string | null;
}

/** Registries — the pickers render straight off these. Order = display order. */
export const CHART_WEIGHT_VARIANTS: readonly ChartWeightVariant[] = [
  {
    id: "standard",
    label: "Standard",
    description: "Fine lines that keep dense charts readable",
    className: "chart-weight-standard",
  },
  {
    id: "bold",
    label: "Bold",
    description: "Heavier lines, easier to follow at a glance",
    className: "chart-weight-bold",
  },
];

export const CHART_COLOR_VARIANTS: readonly ChartColorVariant[] = [
  {
    id: "default",
    label: "Default",
    description: "The Pilot brand palette",
    className: null,
  },
  {
    id: "high_contrast",
    label: "High contrast",
    description: "The same colors, deepened or brightened for your mode, with a firmer grid",
    className: "chart-colors-high-contrast",
  },
];

/** Every class this module may put on `<html>`. */
const ALL_CLASSES = [
  ...CHART_WEIGHT_VARIANTS.map((v) => v.className),
  ...CHART_COLOR_VARIANTS.flatMap((v) => (v.className ? [v.className] : [])),
];

/** The classes that draw charts the way `prefs` say. */
export function chartPrefsClassNames(prefs: ChartPrefs): string[] {
  const weight = CHART_WEIGHT_VARIANTS.find((v) => v.id === prefs.chart_line_weight);
  const colors = CHART_COLOR_VARIANTS.find((v) => v.id === prefs.chart_colors);
  return [weight?.className, colors?.className].filter((c): c is string => !!c);
}

/** The pre-paint script's copy of the last chart classes applied. */
const CACHE_KEY = "chart-classes";

/** Puts exactly `prefs`' chart classes on `<html>`, and records them for pre-paint. */
export function applyChartClasses(prefs: ChartPrefs): void {
  const on = chartPrefsClassNames(prefs);
  const el = document.documentElement;
  for (const cls of ALL_CLASSES) {
    el.classList.toggle(cls, on.includes(cls));
  }
  try {
    localStorage.setItem(CACHE_KEY, on.join(" "));
  } catch {
    // ignore: private-mode storage failures
  }
}
